import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type MobileUsernameAvailability,
  type ProvisionMobileUsernameRequest,
  type ProvisionMobileUsernameResponse,
  type ResetMobileUserPasswordResponse,
  type AppNameValue,
  type InviteMobileUserRequest,
  type InviteMobileUserResponse,
  type MobileUserSummary,
  type UpdateMobileUserRequest,
} from '../client';

/** Stable query-key factory for the mobile-user admin caches. */
export const mobileUserKeys = {
  all: ['directory', 'mobile-users'] as const,
  list: (organisationId: string, app: AppNameValue) =>
    ['directory', 'mobile-users', 'list', organisationId, app] as const,
};

/**
 * Fetch the mobile users in the caller's account for an (org, app). Drives the
 * Settings → Users & Access → Mobile Users tab. `app` defaults to Safety.
 */
export function useMobileUsers(
  organisationId: string | null,
  app: AppNameValue = 'Safety',
  options?: Omit<UseQueryOptions<MobileUserSummary[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<MobileUserSummary[], Error>({
    queryKey: organisationId ? mobileUserKeys.list(organisationId, app) : mobileUserKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getMobileUsers(organisationId, app);
    },
    enabled: !!organisationId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/** POST `/mobile-users/invite`. Invalidates the whole mobile-user cache on success. */
export function useInviteMobileUser() {
  const qc = useQueryClient();
  return useMutation<InviteMobileUserResponse, Error, InviteMobileUserRequest>({
    mutationKey: [...mobileUserKeys.all, 'invite'],
    mutationFn: (body) => directoryApi.inviteMobileUser(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/** PUT `/mobile-users/{id}` — edit display name + role. */
export function useUpdateMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; body: UpdateMobileUserRequest }>({
    mutationKey: [...mobileUserKeys.all, 'update'],
    mutationFn: ({ id, body }) => directoryApi.updateMobileUser(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/** POST `/mobile-users/{id}/suspend`. */
export function useSuspendMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationKey: [...mobileUserKeys.all, 'suspend'],
    mutationFn: (id) => directoryApi.suspendMobileUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/** POST `/mobile-users/{id}/reactivate`. */
export function useReactivateMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationKey: [...mobileUserKeys.all, 'reactivate'],
    mutationFn: (id) => directoryApi.reactivateMobileUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/**
 * DELETE `/mobile-users/{id}` — revokes the user's grant for one (org, app),
 * leaving other apps' grants intact. `app` defaults to Safety.
 */
export function useRemoveMobileUser() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string; app?: AppNameValue }>({
    mutationKey: [...mobileUserKeys.all, 'remove'],
    mutationFn: ({ id, organisationId, app = 'Safety' }) =>
      directoryApi.removeMobileUser(id, organisationId, app),
    onSuccess: () => qc.invalidateQueries({ queryKey: mobileUserKeys.all }),
  });
}

/**
 * Check a username before provisioning. Pass the part after the account code —
 * `JSMITH`, not `ACME-JSMITH`. Returns availability plus suggestions when
 * taken; call with no username to preview the next sequence number.
 *
 * Not a query: it is driven by typing, so the caller decides when to ask.
 */
export function useCheckMobileUsername() {
  return useMutation<MobileUsernameAvailability, Error, string | undefined>({
    mutationFn: (username) => directoryApi.checkMobileUsername(username),
  });
}

/**
 * Provision a username/password mobile user — the route for drivers who will
 * not give a mobile number.
 *
 * The response carries `initialPassword`, which is the ONLY time it is
 * readable: Auth0 keeps the hash and Directory keeps nothing. Callers must
 * show it to the admin and must not discard it silently.
 */
export function useProvisionMobileUsername() {
  const queryClient = useQueryClient();
  return useMutation<ProvisionMobileUsernameResponse, Error, ProvisionMobileUsernameRequest>({
    mutationFn: (body) => directoryApi.provisionMobileUsername(body),
    onSuccess: (_res, body) => {
      queryClient.invalidateQueries({
        queryKey: mobileUserKeys.list(body.organisationId, body.appKey ?? 'Safety'),
      });
    },
  });
}

/**
 * Issue a new password for a username-based user. Same one-time-visibility
 * rule as provisioning — there is no email on these accounts, so no
 * self-service reset exists.
 */
export function useResetMobileUserPassword() {
  return useMutation<
    ResetMobileUserPasswordResponse,
    Error,
    { id: string; password?: string }
  >({
    mutationFn: ({ id, password }) => directoryApi.resetMobileUserPassword(id, password),
  });
}
